import * as React from 'react';
import { useState } from 'react';
import { Checkout } from '@wix/checkout-components';
import { withExperiments } from '@wix/wix-experiments-react';
import classNames from 'classnames';
import { Text } from '@wix/design-system';
import { Experiments } from 'enums';
import { withInjectedRootStore } from 'providers';
import { CheckoutPageProps } from '../CheckoutPageProps';
import { TermOfUse } from '../CheckoutDisclaimer/TermOfUse';
import s from '../CheckoutPage.scss';
import { DoublePurchaseModal } from '../DoublePurchaseModal/DoublePurchaseModal';
import { CreditExplanation } from '../CreditComponent/CreditExplanation';
import {
  CheckoutErrorType,
  CheckoutProvider,
  useCheckoutProps,
  DoublePurchaseInfo,
  getDoublePurchaseInfoDefaultValue,
} from '@wix/premium-purchase-plan-checkout';
import { useTranslation } from 'react-i18next';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { SentryLogger } from 'services/SentryLogger';
import { GTMReporters, withGtmLogger } from 'components/HOCs/withGtmLogger';
import { PageFooter } from 'components/PageFooter/PageFooter';
import { HttpClient } from '@wix/http-client';

const APP_NAME = 'premium-purchase-plan';

const NewCheckoutPageComp: React.FC<CheckoutPageProps> = (props) => {
  const { experiments, rootStore, reportGTM } = props;
  const { t } = useTranslation();
  const [currencyToString] = usePremiumCurrencyFormatter();
  const [doublePurchaseInfo, setDoublePurchaseInfo] =
    useState<DoublePurchaseInfo>(getDoublePurchaseInfoDefaultValue());
  const [isDoublePurchaseModalOpen, setIsDoublePurchaseModalOpen] =
    useState<boolean>(false);
  const { notificationStore } = rootStore;
  const {
    appContext,
    storage,
    isEditorXSpecialDiscountEnabled,
    isVoucherDisclaimerHidden,
    isSecondUpgrade,
    isChangePlan,
    currentProduct,
    currentCycle,
    baseUrl,
    isDayful,
    isDowngradePlanOfDeclineChargeService,
    couponCode,
    isInSale,
    setCurrentCycle,
    removeUnloadListener,
  } = rootStore.appStore;
  const {
    fedopsLogger,
    locale,
    currencyCode,
    currencySettings,
    currentPlanDetails,
    isAnonymous,
    username,
    userGuid,
    siteGuid,
    isUpgrade,
    isDynamicOffering,
    biLogger,
    requestParamsMap,
    siteName,
    eventAggregator,
    replaceDomainVoucherWithPlanDiscount,
    couponServiceFallback,
  } = appContext;
  const { updateNotification, reloadPage } = notificationStore;
  const { theme: themeName } = usePageModeContext();
  const {
    beforeInit,
    onInitError,
    onBeforeSubmit,
    onInitSuccess,
    onSubmitError,
    onSubmitPending,
    onSubmitSuccess,
    orderPageCreation,
    selectedPaymentMethod,
    summaryProps,
    ...checkoutProps
  } = useCheckoutProps({
    checkoutOptions: {
      appName: APP_NAME,
      locale,
      currencyCode,
      currencySettings,
      userDetails: {
        isAnonymous,
        userGuid,
        userName: username,
      },
      siteGuid,
      onPageLoaded: notifyGTM,
    },
    beforeInitOptions: {
      beginCreateOrderPageHttpRequestInteraction: () =>
        fedopsLogger.beginCreateOrderPageHttpRequestInteraction(),
    },
    onBeforeSubmitOptions: {
      currentProduct,
      currentCycle,
      isDynamicOffering,
      siteGuid,
      isUpgrade,
      doublePurchaseInfoDefaultValue: doublePurchaseInfo,
      submitPurchaseClickedBiEventLog: (params) =>
        biLogger.logSubmitPurchaseClickedBiEvent(params),
      doublePurchasePopupViewedLog: (params) =>
        biLogger.logDoublePurchasePopupViewed(params),
      beginSubmitOrderPageInteraction: () =>
        fedopsLogger.beginSubmitOrderPageInteraction(),
      endSubmitOrderPageInteraction: () =>
        fedopsLogger.endSubmitOrderPageInteraction(),
      onDoublePurchase: (newDoublePurchaseInfo: DoublePurchaseInfo) => {
        setIsDoublePurchaseModalOpen(true);
        setDoublePurchaseInfo(newDoublePurchaseInfo);
      },
      logError: SentryLogger.logError,
    },
    onInitErrorOptions: {
      notificationData: {
        defaultMessage: t('app.notification.initialization_error'),
        buttonText: t('app.notification.reload_page'),
        invokeNotification: (notification) => updateNotification(notification),
        reloadPage,
      },
      removeUnloadListener,
      logError: SentryLogger.logError,
    },
    onInitSuccessOptions: {
      currentProduct,
      currentCycle,
      currencyCode,
      endCreateOrderPageHttpRequestInteraction: () =>
        fedopsLogger.endCreateOrderPageHttpRequestInteraction(),
      paymentPageReadyBiEventLog: (params) =>
        biLogger.logPaymentPageReadyBiEvent(params),
      endInitializeOrderPageInteraction: () =>
        fedopsLogger.endInitializeOrderPageInteraction(),
    },
    onSubmitErrorOptions: {
      currentProduct,
      currentCycle,
      removeUnloadListener,
      endSubmitOrderPageInteraction: () =>
        fedopsLogger.endSubmitOrderPageInteraction(),
      logError: SentryLogger.logError,
      biReporters: {
        [CheckoutErrorType.SUBMIT_BUSINESS_ERROR]: (params) =>
          biLogger.logOrderPageBusinessErrorBiEvent(params),
        [CheckoutErrorType.SUBMIT_VALIDATION_ERROR]: (params) =>
          biLogger.logOrderPageValidationErrorBiEvent(params),
        [CheckoutErrorType.SUBMIT_FATAL_ERROR]: (params) =>
          biLogger.logOrderPageFatalErrorBiEvent(params),
      },
    },
    onSubmitPendingOptions: {
      removeUnloadListener,
      endSubmitOrderPageInteraction: () =>
        fedopsLogger.endSubmitOrderPageInteraction(),
    },
    onSubmitSuccessOptions: {
      baseUrl,
      isDayful,
      siteGuid,
      siteName,
      isUpgrade,
      eventAggregator,
      currentProduct,
      currentCycle,
      replaceDomainVoucherWithPlanDiscount,
      isNewMobilePremiumSuccessPageEnabled : experiments.enabled(
        Experiments.NewMobileSearchDomainPage,
      ),
      isPremiumSuccessPageUrlEnabled: experiments.enabled(
        Experiments.enablePremiumSuccessPageUrl,
      ),
      isReassignSuccessPageEnabled: experiments.enabled(
        Experiments.ReassignSuccessPage,
      ),
      isSecondUpgradeNewSuccessPageEnabled: experiments.enabled(
        Experiments.SecondUpgradeNewSuccessPage,
      ),
      isRelativeSepaSuccessRedirectEnabled: experiments.enabled(
        Experiments.RelativeSepaSuccessRedirect,
      ),
      oldSiteGuid: requestParamsMap.oldSiteGuid,
      successUrl: requestParamsMap.successUrl,
      isDowngradePlanOfDeclineChargeService,
      removeUnloadListener,
      onSubmitSuccessStarted: () => reportGTM(GTMReporters.PurchaseSuccess),
      sendPurchasePendingBiLog: (params) =>
        biLogger.logPurchasePendingBiEvent(params),
      sendPurchaseSuccessBiLog: (params) =>
        biLogger.logPurchaseSuccessBiEvent(params),
      endSubmitOrderPageInteraction: () =>
        fedopsLogger.endSubmitOrderPageInteraction(),
    },
    orderPageCreationOptions: {
      siteGuid,
      currentProduct,
      currentCycle,
      isDowngradePlanOfDeclineChargeService,
      ...(isDynamicOffering && {
        dynamicOfferingRequestParams: {
          isUpgrade,
          userPackageGuid: currentPlanDetails?.userPackageGuid,
        },
      }),
      ...(!isDynamicOffering && {
        classicRequestParams: {
          couponCode,
          oldSiteGuid: requestParamsMap.oldSiteGuid,
          existingUserPackageGuid: requestParamsMap.existingUserPackageGuid,
          couponServiceFallback,
        },
      }),
    },
    summaryPropsOptions: {
      tFunction: t,
      currencyToString,
      renderItemsOptions: {
        currentProduct,
        currentCycle,
        isInSale,
        isRemoveStrikePriceEnabled: experiments.enabled(
          Experiments.RemoveStrikedPrice,
        ),
        isCheckoutSavingsEnabled: experiments.enabled(
          Experiments.CheckoutSavings,
        ),
        isNoVatInCheckoutSummeryEnabled: experiments.enabled(
          Experiments.NoVatInCheckoutSummery,
        ),
        logError: SentryLogger.logError,
        SummaryRowComp: Checkout.Summary.Row,
        setCurrentCycle: (options) => setCurrentCycle(options),
      },
      injectedCouponDisplayNameOptions: {
        isInSale,
        currentProduct,
        isEditorXSpecialDiscountEnabled,
      },
      renderCustomFooterOptions: {
        isSecondUpgrade,
        isChangePlan,
      },
      customDisclaimer: <TermOfUse />,
      creditExplanation: <CreditExplanation />,
    },
  });

  async function notifyGTM() {
    storage.getOrderPageViewed().then(
      () => { },
      async () => {
        await storage.setOrderPageViewed();
        reportGTM(GTMReporters.OrderPage);
      },
    );
  }

  return (
    <div
      className={classNames(props.className, s.checkoutContainer, themeName, {
        [s.overrideCheckoutBorders]: !experiments.enabled(
          Experiments.EnablePaymentTypeFiltersSpec,
        ),
      })}
      data-hook='checkout-page'
      data-testid='checkout-page'
    >
      <Checkout
        appName={checkoutProps.appName}
        locale={checkoutProps.locale}
        currencySettings={checkoutProps.currencySettings}
        reporterData={checkoutProps.reporterData}
        beforeInit={beforeInit}
        onBeforeSubmit={onBeforeSubmit}
        onInitError={onInitError}
        onInitSuccess={onInitSuccess}
        onSubmitError={onSubmitError}
        onSubmitPending={onSubmitPending}
        onSubmitSuccess={onSubmitSuccess}
        orderPageCreation={orderPageCreation}
        selectedPaymentMethod={selectedPaymentMethod}
        summaryProps={summaryProps}
        httpClient={new HttpClient()}
      />
      <DoublePurchaseModal
        isOpen={isDoublePurchaseModalOpen}
        onClose={() => setIsDoublePurchaseModalOpen(false)}
        additionalInfo={doublePurchaseInfo}
      />
      <PageFooter>
        {isEditorXSpecialDiscountEnabled && (
          <div>
            <Text
              weight='thin'
              size='tiny'
              dataHook='special-discount-disclaimer'
              secondary
            >
              {t('special_discount_disclaimer')}
            </Text>
          </div>
        )}
        <Text weight='thin' size='tiny' dataHook='legal-disclaimer' secondary>
          {isVoucherDisclaimerHidden
            ? ''
            : t('page_footer.checkout_disclaimer')}
        </Text>
      </PageFooter>
    </div>
  );
};

const withCheckoutProvider = (WrappedComponent) => {
  return (props) => (
    <CheckoutProvider>
      <WrappedComponent {...props} />
    </CheckoutProvider>
  );
};

export const NewCheckoutPage = withInjectedRootStore(
  withGtmLogger(withCheckoutProvider(withExperiments(NewCheckoutPageComp))),
);
